import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { Actions } from './Actions.js';
import { MessageContainerContext } from './contexts/MessageContainerContext.js';
import { Description } from './Description.js';
import type { MessageContainerProps } from './MessageContainer.types.js';
import { Prefix } from './Prefix.js';
import { Title } from './Title.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { devLog } from '../../core/utils/logging.js';
import { Container } from '../../layouts/container/Container.js';
import { Flex } from '../../layouts/flex/Flex.js';

const COMPONENT_NAME = 'MessageContainer';

const _MessageContainer = forwardRef<HTMLDivElement, MessageContainerProps>(
  (props, forwardedRef) => {
    const {
      children,
      variant = 'neutral',
      onDismiss,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dtrum-mask': dataDtrumMask,
      ...remainingProps
    }: MessageContainerProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const intl = useIntl();

    const [slots] = useSlots(children, {
      prefix: Prefix,
      title: Title,
      description: Description,
      actions: Actions,
    });

    if (!slots.description) {
      devLog(
        'The MessageContainer component requires a Description component.',
      );
    }

    const wrapActions = slots.title && slots.description;

    return (
      <MessageContainerContext.Provider value={{ variant }}>
        <Container
          color={variant}
          padding={6}
          paddingLeft={16}
          ref={forwardedRef}
          className={consumerClassName}
          style={consumerStyle}
          data-testid={dataTestId}
          data-dtrum-allow={dataDtrumAllow}
          data-dtrum-mask={dataDtrumMask}
          {...ariaLabelingProps}
          {...behavioralTrackingProps}
          {...forwardProps}
          data-dt-component={COMPONENT_NAME}
        >
          <Flex justifyContent="space-between" alignItems="flex-start">
            {slots.prefix && (
              <Flex flex="0 0 20px" paddingY={6} justifyContent="center">
                {slots.prefix}
              </Flex>
            )}
            <Flex
              flexGrow={1}
              columnGap={8}
              rowGap={6}
              flexDirection={wrapActions ? 'column' : 'row'}
              flexWrap="wrap"
              minWidth={0}
            >
              <Flex
                flexDirection="column"
                gap={2}
                flexGrow={1}
                justifyContent="center"
                paddingY={6}
                paddingRight={8}
                maxWidth="100%"
                minWidth={0}
                style={{ boxSizing: 'border-box' }}
              >
                {slots.title}
                {slots.description}
              </Flex>
              {slots.actions && (
                <Flex
                  {...(wrapActions && {
                    style: { paddingRight: 8, paddingBottom: 10 },
                  })}
                >
                  {slots.actions}
                </Flex>
              )}
            </Flex>
            {onDismiss !== undefined && (
              <Flex flexShrink={0} alignItems="flex-start">
                <Button
                  onClick={onDismiss}
                  color={variant}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Close message',
                    id: '2+ypcBK9NuQ5zg1k',
                    description:
                      'Aria label used for the close button of the information',
                  })}
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </Flex>
            )}
          </Flex>
        </Container>
      </MessageContainerContext.Provider>
    );
  },
);

(
  _MessageContainer as typeof _MessageContainer & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * The `MessageContainer` allows you to communicate statuses prominently. It's either persistent or dismissible. If needed, provide actions to help users take the following steps or resolve an issue.
 * @public
 */
export const MessageContainer = Object.assign(_MessageContainer, {
  Prefix,
  Title,
  Description,
  Actions,
});
